import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { api } from '@/api/client';
import {
  clearStaleSelectedProfilePreference,
  resolveInitialSelectedProfileId,
  setSelectedProfileId,
} from '@/lib/selectedProfile';

// Create context
const GlobalContext = createContext(undefined);

// Provider component
export function GlobalProvider({ children }) {
  const { user: authUser, isAuthenticated, logout: authLogout } = useAuth();
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeContext, setActiveContextState] = useState(null);
  const [currentProfile, setCurrentProfileState] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoadDoneRef = useRef(false);
  /** WP01: invalidate in-flight profile/permission restores after a newer selection. */
  const profileLoadGenRef = useRef(0);

  // Load user data from auth context (no Base44 calls)
  useEffect(() => {
    loadUserData();
  }, [authUser, isAuthenticated]);

  function loadUserData() {
    try {
      if (!initialLoadDoneRef.current) {
        setLoading(true);
      }

      if (!isAuthenticated || !authUser) {
        setLoading(false);
        return;
      }

      // Use the user data from AuthContext / localStorage
      const storedUser = localStorage.getItem('user');
      const userData = storedUser ? JSON.parse(storedUser) : authUser;

      setUser({
        id: userData.id || authUser.id,
        email: userData.email || authUser.email,
        full_name: userData.full_name || userData.email || authUser.email,
        role: userData.role || authUser.role || 'authenticated',
        company_id: userData.company_id || authUser.company_id,
        company_name: userData.company_name || 'My Company'
      });

      // Set company from user data
      const companyId = userData.company_id || authUser.company_id;
      setCompany({
        id: companyId || 'default',
        name: userData.company_name || 'My Company',
        subscription_status: 'pro',
        current_user_count: 1,
        max_users: 10,
        current_account_count: 0,
        max_accounts: 10,
        pricing_plan: {
          name: 'pro',
          price_monthly: 149,
          max_users: 10,
          max_accounts: 10
        }
      });

      // Restore active context from localStorage
      const savedContext = localStorage.getItem('activeContext');
      if (savedContext) {
        try {
          setActiveContextState(JSON.parse(savedContext));
        } catch {
          // ignore parse errors
        }
      }

      const savedProfileId = resolveInitialSelectedProfileId();
      if (savedProfileId) {
        const gen = ++profileLoadGenRef.current;
        api.profiles.get(savedProfileId)
          .then(res => {
            if (gen !== profileLoadGenRef.current) return;
            const profile = res.data.profile;
            if (!profile || profile.status === 'deleted') {
              clearStaleSelectedProfilePreference();
              setSelectedProfileId(null);
              setCurrentProfileState(null);
              return;
            }
            setSelectedProfileId(profile.id);
            setCurrentProfileState(profile);
            const uid = userData.id || authUser.id;
            if (uid) {
              api.permissions.getUser(profile.id, uid)
                .then(permRes => {
                  if (gen !== profileLoadGenRef.current) return;
                  setUserPermissions(permRes.data.permissions);
                })
                .catch(() => {
                  if (gen !== profileLoadGenRef.current) return;
                  setUserPermissions(null);
                });
            }
          })
          .catch(() => {
            if (gen !== profileLoadGenRef.current) return;
            clearStaleSelectedProfilePreference();
            setSelectedProfileId(null);
            setCurrentProfileState(null);
          });
      }

      setLoading(false);
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('Error loading user data:', error);
      setLoading(false);
      initialLoadDoneRef.current = true;
    }
  }

  // Update permissions when profile changes (generation-checked; WP01 ID01).
  function setCurrentProfile(profile) {
    const gen = ++profileLoadGenRef.current;
    setCurrentProfileState(profile);
    setUserPermissions(null);
    if (profile) {
      setSelectedProfileId(profile.id);
      if (user?.id) {
        api.permissions.getUser(profile.id, user.id)
          .then(res => {
            if (gen !== profileLoadGenRef.current) return;
            setUserPermissions(res.data.permissions);
          })
          .catch(() => {
            if (gen !== profileLoadGenRef.current) return;
            setUserPermissions(null);
          });
      }
    } else {
      setSelectedProfileId(null);
    }
  }

  function setActiveContext(context) {
    setActiveContextState(context);
    localStorage.setItem('activeContext', JSON.stringify(context));
    window.location.reload();
  }

  function logout() {
    localStorage.removeItem('activeContext');
    authLogout();
  }

  function hasFeatureAccess(featureKey) {
    if (!company) return false;

    const planFeatures = {
      starter: ['dashboard', 'report_builder'],
      pro: ['dashboard', 'report_builder', 'chat_assistant', 'knowledge_center', 'target_pacing'],
      enterprise: ['dashboard', 'report_builder', 'chat_assistant', 'knowledge_center', 'target_pacing', 'skills', 'audits']
    };

    const plan = company.pricing_plan?.name || 'pro';
    const allowedFeatures = planFeatures[plan] || planFeatures.starter;
    return allowedFeatures.includes(featureKey);
  }

  function refreshData() {
    loadUserData();
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-ad-bg flex items-center justify-center">
        <div className="text-ad-text text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <GlobalContext.Provider
      value={{
        user,
        company,
        activeContext,
        currentProfile,
        setCurrentProfile,
        userPermissions,
        loading,
        setActiveContext,
        logout,
        hasFeatureAccess,
        refreshData,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}

// Hook to use the context
export function useGlobal() {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobal must be used within GlobalProvider');
  }
  return context;
}
